import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Truck, ArrowLeft, Mail, CheckCircle, AlertTriangle } from 'lucide-react';
import { getErrorMessage } from '../lib/utils';
import logoIcon from '../assets/logo-icon.png';
import logoWordmark from '../assets/logo-wordmark.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email);
      setSent(true);
      toast.success('Enlace de recuperación enviado');
      // In dev mode (no Resend), backend returns the token
      if (res.data._dev_reset_token) {
        setDevToken(res.data._dev_reset_token);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Algo salió mal. Inténtalo de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img
              src={logoIcon}
              alt=""
              aria-hidden="true"
              className="h-14 w-auto"
            />
            <img
              src={logoWordmark}
              alt="VehiMax - Todos los Vehículos. Una Plataforma"
              className="h-8 w-auto rounded-sm"
            />
          </Link>

          {!sent ? (
            <>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Recuperar contraseña</h1>
              <p className="text-muted-foreground mt-2">
                Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
              </p>
            </>
          ) : (
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 shrink-0 mt-1" />
              <div>
                <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Revisa tu correo</h1>
                <p className="text-muted-foreground mt-2">
                  Si existe una cuenta para <strong>{email}</strong>, te enviamos un enlace para restablecer tu contraseña.
                </p>
              </div>
            </div>
          )}
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold uppercase tracking-wide">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                placeholder="john@example.com"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-accent w-full h-12"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
              <Mail className="ml-2 h-4 w-4" />
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-accent font-semibold hover:underline">
                <ArrowLeft className="inline h-3 w-3 mr-1" />
                Volver a iniciar sesión
              </Link>
            </p>
          </form>
        ) : (
          <div className="space-y-6">
            {devToken && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md" data-testid="dev-reset-link">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                  <span className="text-sm font-semibold text-yellow-800">Modo desarrollo — Correo no configurado</span>
                </div>
                <p className="text-sm text-yellow-700 mb-2">Usa este enlace para restablecer tu contraseña:</p>
                <Link
                  to={`/reset-password?token=${devToken}`}
                  className="text-sm text-accent font-mono break-all hover:underline"
                  data-testid="dev-reset-password-link"
                >
                  Enlace para restablecer contraseña
                </Link>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                onClick={() => { setSent(false); setDevToken(null); }}
                className="w-full h-12"
                data-testid="try-another-email-btn"
              >Probar con otro correo</Button>
              <Link to="/login" className="w-full">
                <Button variant="ghost" className="w-full h-12">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Volver a iniciar sesión
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
